// frontend/src/components/UniversalDataEntryForm.js
// Universal data entry form for parameters from multiple categories

import React, { useState, useCallback, useMemo } from 'react';
import { 
  Form, 
  Input, 
  InputNumber, 
  Select, 
  Radio, 
  Card, 
  Button, 
  Space, 
  Typography, 
  Tag, 
  Divider,
  Row,
  Col,
  Tooltip,
  message
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  InfoCircleOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import MomentDatePicker from './MomentDatePicker';

const { Text, Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

/**
 * Universal Data Entry Form Component
 * Handles data entry for parameters from multiple categories in a single interface
 */
const UniversalDataEntryForm = ({
  selectedParameters = [],
  onSave,
  onCancel,
  onBack,
  patientId,
  initialTimestamp = moment(),
  loading = false
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [entryTimestamp, setEntryTimestamp] = useState(initialTimestamp);


  // Group parameters by category for organized display
  const parametersByCategory = useMemo(() => {
    const grouped = {};
    selectedParameters.forEach(param => {
      if (!grouped[param.category]) {
        grouped[param.category] = [];
      }
      grouped[param.category].push(param);
    });
    return grouped;
  }, [selectedParameters]);

  // Get category color for visual organization
  const getCategoryColor = (category) => {
    const colorMap = {
      'Vitals': 'blue',
      'Labs': 'green', 
      'Biometrics': 'orange',
      'Physical Exam': 'purple',
      'Imaging': 'cyan',
      'Lifestyle': 'magenta'
    };
    return colorMap[category] || 'default';
  };

  // Get appropriate input component based on parameter type and category
  const getInputComponent = (parameter) => {
    const { key, category, type, units, options } = parameter;

    // Disable Y-Balance average fields
    if (key.startsWith('biometrics_balance_ybt_') && (key.endsWith('_avg') || key.endsWith('_deviation') || key.endsWith('_asymmetry_cm') || key.includes('_composite_reach'))) {
      return (
        <InputNumber
          style={{ width: '100%' }}
          placeholder="Auto-calculated"
          addonAfter={units}
          disabled
        />
      );
    }

    if (key === 'biometrics_biomeasurement_waistHipRatio') {
      return (
        <InputNumber
          style={{ width: '100%' }}
          placeholder="Auto-calculated"
          addonAfter={units}
          disabled
        />
      );
    }

    if (key === 'biometrics_biomeasurement_ree' || key === 'biometrics_biomeasurement_tee') {
      return (
        <InputNumber
          style={{ width: '100%' }}
          placeholder="Auto-calculated"
          addonAfter={units}
          disabled
        />
      );
    }

    if (key === 'labs_lipid_non_hdl' || key === 'labs_lipid_tc_glu_ratio') {
      return (
        <InputNumber
          style={{ width: '100%' }}
          placeholder="Auto-calculated"
          addonAfter={units}
          disabled
        />
      );
    }

    // Handle different parameter types
    if (options && Array.isArray(options)) {
      // Parameters with predefined options (lifestyle, physical exam)
      if (options.length <= 4) {
        return (
          <Radio.Group optionType="button" buttonStyle="solid">
            {options.map(option => (
              <Radio.Button key={option.value} value={option.value}>
                {option.label}
              </Radio.Button>
            ))}
          </Radio.Group>
        );
      } else {
        return (
          <Select placeholder="Select option" allowClear>
            {options.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        );
      }
    }

    // Numeric parameters (vitals, labs, biometrics)
    if (category === 'Vitals' || category === 'Labs' || category === 'Biometrics') {
      return (
        <InputNumber
          style={{ width: '100%' }}
          placeholder={`Enter ${parameter.displayName || parameter.label}`}
          addonAfter={units}
          precision={getNumericPrecision(parameter)}
          min={getMinValue(parameter)}
          max={getMaxValue(parameter)}
        />
      );
    }

    // Text-based parameters (imaging findings, notes)
    if (category === 'Imaging' || type === 'text') {
      return (
        <TextArea
          rows={2}
          placeholder={`Enter ${parameter.displayName || parameter.label}`}
          maxLength={500}
          showCount
        />
      );
    }

    // Default to text input
    return (
      <Input
        placeholder={`Enter ${parameter.displayName || parameter.label}`}
        maxLength={100}
      />
    );
  };

  // Get numeric precision based on parameter type
  const getNumericPrecision = (parameter) => {
    const { key } = parameter;

    // Integer-only parameters
    const integerKeys = [
      'vitals_rhr', 'vitals_rr', 'vitals_sirs', 'vitals_pain',
      'biometrics_cognition_tmtA_errors', 'biometrics_cognition_tmtB_errors',
      'biometrics_cognition_wordRecall_immediate1', 'biometrics_cognition_wordRecall_immediate2',
      'biometrics_cognition_wordRecall_delayed',
      'biometrics_cognition_stroop_word_errors', 'biometrics_cognition_stroop_color_errors',
      'biometrics_cognition_stroop_interference_errors',
      'biometrics_strength_pushups_completed',
      'biometrics_endurance_pushups_completed',
      'biometrics_endurance_step_test_duration',
      'biometrics_endurance_heart_rate_peak',
      'biometrics_endurance_heart_rate_1min',
      'biometrics_endurance_heart_rate_1min_post_exercise'
    ];

    if (integerKeys.includes(key)) {
      return 0;
    }

    // High precision for specific lab values
    if (key.includes('hba1c') || key.includes('creatinine') || key.includes('potassium') || key.includes('chloride') || key.includes('bicarb')) {
      return 2;
    }
    
    // Standard precision for most other numeric parameters
    return 1;
  };

  // Get minimum value constraints
  const getMinValue = (parameter) => {
    const { category, key } = parameter;
    
    if (category === 'Vitals') {
      if (key.includes('bp') || key.includes('pressure')) return 0;
      if (key.includes('temp')) return 30;
      if (key.includes('weight')) return 0;
      if (key.includes('height')) return 0;
    }
    
    return 0;
  };

  // Get maximum value constraints
  const getMaxValue = (parameter) => {
    const { category, key } = parameter;
    
    if (category === 'Vitals') {
      if (key.includes('sbp')) return 300;
      if (key.includes('dbp')) return 200;
      if (key.includes('rhr')) return 250;
      if (key.includes('temp')) return 45;
      if (key.includes('spo2')) return 100;
    }
    
    return undefined; // No max limit for most parameters
  };

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    try {
      await form.validateFields();

      // Validate entry timestamp
      if (!entryTimestamp || !entryTimestamp.isValid()) {
        message.error('Please select a valid entry date and time before saving.');
        return;
      }

      if (entryTimestamp.isAfter(moment())) {
        message.error('Entry date cannot be in the future. Please select a current or past date.');
        return;
      }

      // Check if the date is too far in the past (more than 10 years)
      if (entryTimestamp.isBefore(moment().subtract(10, 'years'))) {
        message.warning('Entry date is more than 10 years in the past. Please verify this is correct.');
        // Don't return - allow the save but warn the user
      }

      const allValues = form.getFieldsValue(true); // Get all values, including disabled fields

      // Filter out empty values
      const filteredValues = {};
      Object.keys(allValues).forEach(key => {
        if (allValues[key] !== undefined && allValues[key] !== null && allValues[key] !== '') {
          filteredValues[key] = allValues[key];
        }
      });

      if (Object.keys(filteredValues).length === 0) {
        message.warning('Please enter at least one parameter value.');
        return;
      }

      // Organize data by category for backend processing
      const organizedData = {};
      selectedParameters.forEach(param => {
        const value = filteredValues[param.key];
        if (value !== undefined) {
          if (!organizedData[param.category]) {
            organizedData[param.category] = {};
          }
          organizedData[param.category][param.key] = value;
        }
      });

      // Call save handler with organized data
      await onSave({
        timestamp: entryTimestamp.toISOString(),
        parameterData: organizedData,
        selectedParameters,
        rawValues: filteredValues
      });

      message.success(`Successfully saved ${Object.keys(filteredValues).length} parameter(s)!`);
      
    } catch (error) {
      console.error('Form validation failed:', error);
      message.error('Please check all required fields and try again.');
    }
  }, [form, selectedParameters, entryTimestamp, onSave]);

  // Get validation rules for parameter
  const getValidationRules = (parameter) => {
    const rules = [];
    
    // Skip validation for auto-calculated fields
    if (parameter.key.endsWith('_avg') || 
        parameter.key.endsWith('_deviation') || 
        parameter.key.endsWith('_asymmetry_cm') || 
        parameter.key.includes('_composite_reach') ||
        parameter.key === 'biometrics_biomeasurement_waistHipRatio' ||
        parameter.key === 'biometrics_biomeasurement_ree' ||
        parameter.key === 'biometrics_biomeasurement_tee') {
      return rules; // No validation for auto-calculated fields
    }
    
    // Add required rule for all selected parameters (since user chose to enter data for them)
    rules.push({ 
      required: true, 
      message: `${parameter.displayName || parameter.label || parameter.name} is required` 
    });

    // Add numeric validation for numeric parameters
    if (parameter.category === 'Vitals' || parameter.category === 'Labs' || parameter.category === 'Biometrics') {
      rules.push({ 
        type: 'number', 
        message: 'Please enter a valid number' 
      });
    }

    return rules;
  };

  return (
    <div>
      {/* Header */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={onBack}
                type="text"
              >
                {t('buttons.backToSelection')}
              </Button>
              <Divider type="vertical" />
              <Title level={4} style={{ margin: 0 }}>
                {t('forms.enterDataFor', 'Enter Data for {{count}} Parameter(s)', { count: selectedParameters.length })}
              </Title>
            </Space>
          </Col>
          <Col>
            <Space align="center">
              <CalendarOutlined style={{ color: '#1890ff' }} />
              <Text type="secondary" strong>{t('forms.entryDateTime')}</Text>
              <MomentDatePicker
                showTime={{
                  format: 'HH:mm',
                  defaultValue: moment().startOf('hour')
                }}
                format="YYYY-MM-DD HH:mm"
                value={entryTimestamp}
                onChange={(date) => {
                  if (date && date.isAfter(moment())) {
                    message.warning(t('forms.futureDateWarning'));
                    return;
                  }
                  setEntryTimestamp(date || moment());
                }}
                placeholder={t('forms.selectEntryDateTime')}
                style={{ width: 220 }}
                disabledDate={(current) => current && current.isAfter(moment().endOf('day'))}
                disabledTime={(current) => {
                  if (current && current.isSame(moment(), 'day')) {
                    const now = moment();
                    return {
                      disabledHours: () => {
                        const hours = [];
                        for (let i = now.hour() + 1; i < 24; i++) {
                          hours.push(i);
                        }
                        return hours;
                      },
                      disabledMinutes: (selectedHour) => {
                        if (selectedHour === now.hour()) {
                          const minutes = [];
                          for (let i = now.minute() + 1; i < 60; i++) {
                            minutes.push(i);
                          }
                          return minutes;
                        }
                        return [];
                      }
                    };
                  }
                  return {};
                }}
                allowClear={false}
                size="default"
              />
              <Tooltip title="Select the date and time when this data was collected. Cannot be in the future. Use this to back-date entries for historical data recording.">
                <InfoCircleOutlined style={{ color: '#1890ff' }} />
              </Tooltip>
              {!entryTimestamp.isSame(moment(), 'day') && (
                <Tooltip title="Historical entry: This data will be recorded with a past date">
                  <Text type="warning" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                    📅 Historical Entry
                  </Text>
                </Tooltip>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Data Entry Form */}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        scrollToFirstError
        onValuesChange={(changedValues, allValues) => {
          const changedKey = Object.keys(changedValues)[0];
          if (changedKey && changedKey.startsWith('biometrics_balance_ybt_') && !changedKey.endsWith('_avg')) {
            const parts = changedKey.split('_');
            parts.pop(); // remove trial number
            const baseKey = parts.join('_');
            
            const trial1Key = `${baseKey}_1`;
            const trial2Key = `${baseKey}_2`;
            const avgKey = `${baseKey}_avg`;

            const trial1Value = allValues[trial1Key];
            const trial2Value = allValues[trial2Key];

            if (trial1Value !== undefined && trial1Value !== null && trial2Value !== undefined && trial2Value !== null) {
              const avg = (parseFloat(trial1Value) + parseFloat(trial2Value)) / 2;
              if (!isNaN(avg)) {
                form.setFieldsValue({ [avgKey]: avg.toFixed(1) });
              }
            } else {
              form.setFieldsValue({ [avgKey]: null });
            }
          }
        }}
      >
        {Object.entries(parametersByCategory).map(([category, parameters]) => (
          <Card
            key={category}
            title={
              <Space>
                <Tag color={getCategoryColor(category)}>{category}</Tag>
                <Text strong>{parameters.length} parameter(s)</Text>
              </Space>
            }
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Row gutter={[16, 16]}>
              {parameters.map((parameter) => (
                <Col 
                  key={parameter.key} 
                  xs={24} 
                  sm={12} 
                  md={8} 
                  lg={6}
                >
                  <Form.Item
                    name={parameter.key}
                    label={
                      <Space>
                        <Text strong>{parameter.displayName || parameter.label}</Text>
                        {parameter.units && (
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            ({parameter.units})
                          </Text>
                        )}
                        {parameter.description && (
                          <Tooltip title={parameter.description}>
                            <InfoCircleOutlined style={{ color: '#1890ff' }} />
                          </Tooltip>
                        )}
                      </Space>
                    }
                    rules={getValidationRules(parameter)}
                  >
                    {getInputComponent(parameter)}
                  </Form.Item>
                </Col>
              ))}
            </Row>
          </Card>
        ))}

        {/* Action Buttons */}
        <Card size="small">
          <Row justify="end">
            <Col>
              <Space>
                <Button onClick={onCancel}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={onBack}>
                  {t('buttons.backToSelection')}
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={loading}
                  size="large"
                >
                  {t('buttons.saveAllData')}
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>
      </Form>
    </div>
  );
};

export default UniversalDataEntryForm;
